import { DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataTableColumn } from '../../../shared/components';
import { UserService } from '../../../core/services/user.service';
import { ServiceCreditItem, ServiceCreditTypesEnum, User } from '../../../core/models/user.model';

enum CreditTypesEnum {
    LicenseCreditsPerMonth = 'License Credits (per month)',
    LicenseCreditsRemaining = 'License Credits Remaining',
    AccountSharedCreditsTotal = 'Account Shared Credits (Total)',
    AccountSharedCreditsRemaining = 'Account Shared Credits Remaining',
    AccountSharedCreditsAssignedToYou = 'Account Shared Credits Assigned to You',
    YourAccountSharedCreditsRemaining = 'Your Account Shared Credits Remaining',
}

@UntilDestroy()
@Component({
    selector: 'app-credit-balance-modal',
    templateUrl: './credit-balance-modal.component.html',
    styleUrls: ['./credit-balance-modal.component.scss'],
    providers: [DecimalPipe],
    standalone: false
})
export class CreditBalanceModalComponent implements OnInit {
    credits: ServiceCreditItem[] = [];
    hasLicenceCredits: boolean = false;
    accountRenewDate;
    licenseRenewDate;
    tableData = [];
    columns: DataTableColumn[] = [
        {
            dataField: 'creditType',
            caption: 'Credit Type',
            isSticky: true,
            width: '290px',
            cellClasses: 'border-end',
            headerCellClasses: 'border-end',
        },
    ]

    constructor(
        private readonly userService: UserService,
        private readonly decimalPipe: DecimalPipe,
    ) { }

    ngOnInit() {
        this.userService.getUserData().pipe(untilDestroyed(this)).subscribe((user: User) => {
            this.hasLicenceCredits = user['has_licence_credits'];
            user['service_credits']?.forEach((credit: ServiceCreditItem) => {
                if (credit.ad_hoc_credits > 0 || credit.license_credit_limit > 0 || credit.total_account_credit_limit > 0) {
                    this.credits.push(credit)
                }
            });
            this.accountRenewDate = user['billing_period_end'];
            this.licenseRenewDate = this.hasLicenceCredits ? user['license_renew_date'] : '';

            this.buildColumns();
            this.buildTableData();
        });
    }

    private buildColumns(): void {
        this.columns = [
            ...this.columns,
            ...this.credits
                .filter((credit: ServiceCreditItem) => credit.ad_hoc_credits > 0 || credit.license_credit_limit > 0 || credit.total_account_credit_limit > 0)
                .map((credit: ServiceCreditItem) => {
                    let caption: string = `${credit.proxy_service_name} ${credit.credit_type}`;

                    switch (credit.credit_type) {
                        case ServiceCreditTypesEnum.COMBINED:
                            caption = 'New Contacts / Exports';
                            break;
                        case ServiceCreditTypesEnum.ROD:
                            caption = 'Research on Demand';
                            break;
                        case ServiceCreditTypesEnum.ENRICHMENT:
                            caption = 'Enrichment';
                            break;
                        case ServiceCreditTypesEnum.BOUNCEINTEL:
                            caption = 'BounceIntel';
                            break;
                    }

                    return {
                        caption,
                        dataField: credit.credit_type,
                        width: '290px',
                        allowSorting: false,
                    };
                }),
        ]
    }

    private buildTableData(): void {
        this.tableData = [
            this.hasLicenceCredits ? CreditTypesEnum.LicenseCreditsPerMonth : null,
            this.hasLicenceCredits ? CreditTypesEnum.LicenseCreditsRemaining : null,
            CreditTypesEnum.AccountSharedCreditsTotal,
            CreditTypesEnum.AccountSharedCreditsRemaining,
            CreditTypesEnum.AccountSharedCreditsAssignedToYou,
            CreditTypesEnum.YourAccountSharedCreditsRemaining,
        ].filter(Boolean).map((creditType: CreditTypesEnum) => ({
            creditType,
            ...this.credits.reduce((acc: Record<string, string | number>, credit: ServiceCreditItem) => ({
                ...acc,
                [credit.credit_type]: this.getRowValueByCreditType(creditType, credit),
            }), {}),
        }));
    }

    private getRowValueByCreditType(creditType: CreditTypesEnum, credit: ServiceCreditItem): string | number {
        switch (creditType) {
            case CreditTypesEnum.LicenseCreditsPerMonth:
                return (credit.license_credit_limit === 0) ? '-' : this.decimalPipe.transform(credit.license_credit_limit);
            case CreditTypesEnum.LicenseCreditsRemaining:
                return credit.license_credit_limit === 0 ? '-' : this.decimalPipe.transform(credit.license_credit_limit - credit.license_credits_used);
            case CreditTypesEnum.AccountSharedCreditsTotal:
                return (credit.total_account_credit_limit === 0) ? '-' : this.decimalPipe.transform(credit.total_account_credit_limit);
            case CreditTypesEnum.AccountSharedCreditsRemaining:
                return (credit.total_account_credit_limit === 0) ? '-' : this.decimalPipe.transform(credit.account_credits_remaining);
            case CreditTypesEnum.AccountSharedCreditsAssignedToYou:
                // @ts-ignore TODO: check this if block for existing fields (lengthUnits, length) in user credits
                if (credit.lengthUnits) {
                    return this.decimalPipe.transform(credit.total_credit_limit) + ' per contract period' + ((credit['length'] !==
                        null &&
                        credit['length'] > 0) ? ('; ' +
                        credit['length'] + ' per ' + credit['lengthUnits'].toLowerCase()) : '');
                }
                return (credit.total_credit_limit === 0) ? '-' : this.decimalPipe.transform(credit.total_credit_limit) + ' per contract period';
            case CreditTypesEnum.YourAccountSharedCreditsRemaining:
                return (credit.total_credit_limit === 0) ? '-' : this.decimalPipe.transform(credit.credits_remaining);
        }
    }
}
